/**
 * EUNACOM Landing Page
 * Página de presentación del módulo de preparación EUNACOM
 */
import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  GraduationCap, Rocket, CheckCircle, Brain, Target, 
  Clock, BarChart3, BookOpen, Shield, Users, 
  Stethoscope, Activity, Heart, ChevronRight
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EunacomLandingPage = () => {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [specialties, setSpecialties] = useState([]);
  const [packs, setPacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [ufConfig, setUfConfig] = useState(null);

  useEffect(() => {
    fetchData();
    fetchUfConfig();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, specialtiesRes, packsRes] = await Promise.all([
        fetch(`${API_URL}/api/eunacom/plans/public`),
        fetch(`${API_URL}/api/eunacom/specialties/public`),
        fetch(`${API_URL}/api/eunacom/packs/public`)
      ]);

      if (plansRes.ok) {
        const data = await plansRes.json();
        setPlans(data.plans || []);
      }
      if (specialtiesRes.ok) {
        const data = await specialtiesRes.json();
        setSpecialties(data.specialties || []);
      }
      if (packsRes.ok) {
        const data = await packsRes.json();
        setPacks(data.packs || []);
      }
    } catch (error) {
      console.error('Error fetching EUNACOM data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUfConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/uf/current`);
      if (response.ok) {
        const data = await response.json();
        setUfConfig(data);
      }
    } catch (error) {
      console.error('Error fetching UF config:', error);
    }
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setShowRegisterModal(true);
  };

  // Formatear precio en UF con formato chileno (0,99 UF)
  const formatUF = (value, decimals = 2) => {
    if (value === null || value === undefined) return null;
    const formatted = value.toFixed(decimals).replace('.', ',');
    return `${formatted} UF`;
  };

  // Formatear precio CLP (solo para referencia)
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(price);
  };

  // Obtener precio en UF del plan EUNACOM
  const getPlanPriceUF = (plan) => {
    return plan?.pricing?.precio_uf || 0.99;
  };

  // Obtener precio del pack en UF
  const getPackPriceUF = (pack) => {
    return pack?.precio_uf || 0;
  };

  const getIconComponent = (iconName) => {
    const icons = {
      GraduationCap: GraduationCap,
      Rocket: Rocket,
      Brain: Brain,
      Target: Target,
      Clock: Clock,
      BarChart3: BarChart3,
      BookOpen: BookOpen,
      Shield: Shield,
      Users: Users,
      Stethoscope: Stethoscope,
      Activity: Activity,
      Heart: Heart
    };
    return icons[iconName] || GraduationCap;
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 rounded-lg flex items-center justify-center">
              <GraduationCap className="w-6 h-6 text-white" />
            </div>
            <div>
              <span className="text-white font-bold text-lg">SimuClinic</span>
              <span className="text-emerald-400 font-semibold ml-1">EUNACOM</span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <button 
              onClick={() => navigate('/login')}
              className="text-slate-300 hover:text-white transition-colors"
            >
              Iniciar Sesión
            </button>
            <button 
              onClick={() => setShowRegisterModal(true)}
              className="bg-emerald-600 hover:bg-emerald-500 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Comenzar Ahora
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-block px-4 py-2 bg-emerald-600/20 text-emerald-400 rounded-full text-sm font-medium mb-6">
              Preparación EUNACOM
            </span>
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
              No memorices solamente:
              <br />
              <span className="text-emerald-400">practica cómo tomar decisiones clínicas</span>
            </h1>
            <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
              Prepárate mediante casos que evolucionan según tus decisiones. 
              Identifica tus áreas débiles antes del examen y mejora con cada simulación.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setShowRegisterModal(true)}
                className="bg-emerald-600 hover:bg-emerald-500 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all shadow-lg shadow-emerald-600/30 flex items-center justify-center gap-2"
              >
                <GraduationCap className="w-5 h-5" />
                Comenzar mi preparación
              </button>
              <button
                onClick={() => document.getElementById('planes').scrollIntoView({ behavior: 'smooth' })}
                className="border border-slate-600 hover:border-emerald-500 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all"
              >
                Ver planes
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            ¿Por qué prepararte con SimuClinic?
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Brain,
                title: "Razonamiento Clínico Real",
                description: "Casos interactivos que responden a tus decisiones, no solo preguntas de selección múltiple."
              },
              {
                icon: Target,
                title: "Identifica tus Debilidades",
                description: "Estadísticas detalladas por especialidad para saber exactamente dónde enfocarte."
              },
              {
                icon: BarChart3,
                title: "Seguimiento de Progreso",
                description: "Visualiza tu evolución semana a semana y compara tus resultados anteriores."
              },
              {
                icon: Clock,
                title: "Practica con Tiempo",
                description: "Modo evaluación con tiempo limitado para simular las condiciones reales del examen."
              },
              {
                icon: BookOpen,
                title: "Retroalimentación Educativa",
                description: "Explicaciones detalladas después de cada caso para consolidar tu aprendizaje."
              },
              {
                icon: Shield,
                title: "Plan de Estudio Personalizado",
                description: "Recomendaciones automáticas basadas en tu desempeño y fecha de examen."
              }
            ].map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-slate-700/50 rounded-2xl p-6 border border-slate-600 hover:border-emerald-500/50 transition-colors"
              >
                <div className="w-12 h-12 bg-emerald-600/20 rounded-xl flex items-center justify-center mb-4">
                  <benefit.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{benefit.title}</h3>
                <p className="text-slate-400">{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Specialties Section */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Especialidades Disponibles
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Practica con casos de todas las áreas evaluadas en el EUNACOM
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {specialties.slice(0, 12).map((specialty, index) => (
              <motion.div
                key={specialty.codigo}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                className="px-4 py-2 rounded-full text-sm font-medium border"
                style={{ 
                  backgroundColor: `${specialty.color}20`,
                  borderColor: specialty.color,
                  color: specialty.color
                }}
              >
                {specialty.nombre}
              </motion.div>
            ))}
            {specialties.length > 12 && (
              <div className="px-4 py-2 rounded-full bg-slate-700 text-slate-300 text-sm font-medium">
                +{specialties.length - 12} más
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="planes" className="py-20 px-4 bg-slate-800/50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-4">
            Planes de Preparación
          </h2>
          <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
            Elige el plan que mejor se adapte a tu tiempo de preparación
          </p>
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan, index) => {
              const Icon = getIconComponent(plan.icono);
              return (
                <motion.div
                  key={plan.id || plan.codigo}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className={`relative bg-slate-800 rounded-2xl border overflow-hidden ${
                    plan.etiqueta_destacada 
                      ? 'border-emerald-500 shadow-lg shadow-emerald-500/20' 
                      : 'border-slate-700'
                  }`}
                >
                  {plan.etiqueta_destacada && (
                    <div className="absolute top-0 right-0 bg-emerald-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                      {plan.etiqueta_destacada}
                    </div>
                  )}
                  
                  <div className="p-8">
                    <div className="flex items-center gap-3 mb-4">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                        style={{ backgroundColor: `${plan.color}20` }}
                      >
                        <Icon className="w-6 h-6" style={{ color: plan.color }} />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-white">{plan.nombre}</h3>
                        <p className="text-slate-400 text-sm">{plan.vigencia_dias} días</p>
                      </div>
                    </div>
                    
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-white">
                        {formatUF(getPlanPriceUF(plan))}
                      </span>
                      <span className="text-slate-400 ml-2">
                        IVA incluido
                      </span>
                      {plan.codigo === 'eunacom_individual' && (
                        <span className="text-slate-500 block text-sm mt-1">/mes</span>
                      )}
                    </div>
                    
                    <p className="text-slate-300 mb-6">{plan.descripcion_breve}</p>
                    
                    <ul className="space-y-3 mb-8">
                      {(plan.features_destacadas || plan.features?.slice(0, 5) || []).map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-slate-300 text-sm">
                          <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    
                    <button
                      onClick={() => handleSelectPlan(plan)}
                      className={`w-full py-3 rounded-xl font-semibold transition-all flex items-center justify-center gap-2 ${
                        plan.etiqueta_destacada
                          ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                          : 'bg-slate-700 hover:bg-slate-600 text-white'
                      }`}
                    >
                      {plan.texto_boton || 'Comenzar'}
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Additional Packs Section */}
      {packs.length > 0 && (
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-white text-center mb-8">
              ¿Necesitas más simulaciones?
            </h3>
            <div className="grid md:grid-cols-3 gap-4">
              {packs.map((pack) => (
                <div 
                  key={pack.codigo}
                  className="bg-slate-800/50 rounded-xl p-6 border border-slate-700 text-center"
                >
                  <div className="text-3xl font-bold text-emerald-400 mb-2">
                    {pack.simulaciones}
                  </div>
                  <div className="text-slate-400 text-sm mb-4">simulaciones</div>
                  <div className="text-xl font-bold text-white mb-2">
                    {formatUF(getPackPriceUF(pack))}
                  </div>
                  <div className="text-slate-500 text-xs">
                    IVA incluido
                  </div>
                </div>
              ))}
            </div>
            <p className="text-center text-slate-400 mt-6 text-sm">
              Los packs adicionales se pueden adquirir en cualquier momento para ampliar tu práctica.
              <br />
              <span className="text-slate-500 text-xs">Precio final expresado en UF, IVA incluido.</span>
            </p>
          </div>
        </section>
      )}

      {/* Team Section */}
      <section className="py-16 px-4 bg-slate-800/30">
        <div className="max-w-3xl mx-auto text-center">
          <Users className="w-12 h-12 text-emerald-400 mx-auto mb-6" />
          <h3 className="text-2xl font-bold text-white mb-4">
            Desarrollado por un equipo multidisciplinario
          </h3>
          <p className="text-slate-300 mb-4">
            Los escenarios y funcionalidades de SimuClinic son desarrollados y revisados por un equipo 
            que integra experiencia clínica, formación en salud y desarrollo tecnológico.
          </p>
          <div className="flex justify-center gap-6 flex-wrap mt-8">
            {['Médicos', 'Enfermeras', 'Ingenieros', 'Especialistas en Simulación'].map((role) => (
              <span key={role} className="px-4 py-2 bg-slate-700/50 rounded-lg text-slate-300 text-sm">
                {role}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-8 px-4 border-t border-slate-700">
        <div className="max-w-4xl mx-auto">
          <div className="bg-slate-800/50 rounded-xl p-6 text-center">
            <p className="text-slate-400 text-sm mb-4">
              <strong className="text-slate-300">Aviso importante:</strong> SimuClinic es una plataforma 
              independiente de preparación y simulación clínica. No pertenece, representa ni se encuentra 
              afiliada oficialmente a EUNACOM, ASOFAMECH ni a organismos públicos.
            </p>
            <p className="text-slate-500 text-xs">
              Los contenidos tienen fines educativos y de entrenamiento. No garantizan la aprobación del 
              examen ni reemplazan el estudio de bibliografía, guías clínicas, normativas oficiales o 
              programas académicos.
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-slate-700">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <GraduationCap className="w-6 h-6 text-emerald-500" />
            <span className="text-white font-semibold">SimuClinic EUNACOM</span>
          </div>
          <div className="flex gap-6 text-sm text-slate-400">
            <a href="#" className="hover:text-white transition-colors">Términos</a>
            <a href="#" className="hover:text-white transition-colors">Privacidad</a>
            <a href="#" className="hover:text-white transition-colors">Contacto</a>
          </div>
          <p className="text-slate-500 text-sm">
            © 2026 SimuClinic. Todos los derechos reservados.
          </p>
        </div>
      </footer>

      {/* Register Modal */}
      {showRegisterModal && (
        <EunacomRegisterModal 
          onClose={() => setShowRegisterModal(false)}
          selectedPlan={selectedPlan}
        />
      )}
    </div>
  );
};

// Register Modal Component
const EunacomRegisterModal = ({ onClose, selectedPlan }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    email: '',
    password: '',
    confirmPassword: '',
    pais: 'Chile',
    ciudad: '',
    universidad_egreso: '',
    ano_egreso: new Date().getFullYear() - 1,
    nacionalidad: 'Chilena',
    fecha_estimada_examen: '',
    areas_reforzar: [],
    acepta_terminos: false,
    acepta_privacidad: false,
    acepta_uso_individual: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden');
      return;
    }

    if (!formData.acepta_terminos || !formData.acepta_privacidad || !formData.acepta_uso_individual) {
      setError('Debe aceptar todos los términos y condiciones');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/eunacom/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          fecha_estimada_examen: formData.fecha_estimada_examen || null
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Error al registrar');
      }

      // Guardar token y redirigir
      localStorage.setItem('token', data.access_token);
      localStorage.setItem('eunacom_user', JSON.stringify({
        id: data.user_id,
        nombre: formData.nombre,
        email: formData.email
      }));

      navigate('/eunacom/dashboard');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-slate-800 rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-slate-700"
      >
        <div className="p-6 border-b border-slate-700 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Registro EUNACOM</h2>
            <p className="text-slate-400 text-sm">Paso {step} de 2</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </button>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-500/20 border border-red-500 text-red-300 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Nombre</label>
                  <input
                    type="text"
                    name="nombre"
                    value={formData.nombre}
                    onChange={handleChange}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Apellido</label>
                  <input
                    type="text"
                    name="apellido"
                    value={formData.apellido}
                    onChange={handleChange}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Correo electrónico</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Contraseña</label>
                  <input
                    type="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Confirmar</label>
                  <input
                    type="password"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Universidad de egreso</label>
                  <input
                    type="text"
                    name="universidad_egreso"
                    value={formData.universidad_egreso}
                    onChange={handleChange}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Año de egreso</label>
                  <input
                    type="number"
                    name="ano_egreso"
                    value={formData.ano_egreso}
                    onChange={handleChange}
                    min={1990}
                    max={new Date().getFullYear()}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                    required
                  />
                </div>
              </div>

              <button
                onClick={() => setStep(2)}
                disabled={!formData.nombre || !formData.email || !formData.password || !formData.universidad_egreso}
                className="w-full bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition-colors"
              >
                Continuar
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-slate-400 mb-1">País</label>
                  <select
                    name="pais"
                    value={formData.pais}
                    onChange={handleChange}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                  >
                    <option value="Chile">Chile</option>
                    <option value="Argentina">Argentina</option>
                    <option value="Perú">Perú</option>
                    <option value="Colombia">Colombia</option>
                    <option value="Otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm text-slate-400 mb-1">Ciudad</label>
                  <input
                    type="text"
                    name="ciudad"
                    value={formData.ciudad}
                    onChange={handleChange}
                    className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Fecha estimada del examen (opcional)</label>
                <input
                  type="date"
                  name="fecha_estimada_examen"
                  value={formData.fecha_estimada_examen}
                  onChange={handleChange}
                  className="w-full bg-slate-700 border border-slate-600 rounded-lg px-4 py-2 text-white focus:outline-none focus:border-emerald-500"
                />
              </div>

              <div className="space-y-3 pt-4 border-t border-slate-700">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="acepta_terminos"
                    checked={formData.acepta_terminos}
                    onChange={handleChange}
                    className="mt-1 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-slate-300 text-sm">
                    Acepto los <a href="#" className="text-emerald-400 hover:underline">términos y condiciones</a>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="acepta_privacidad"
                    checked={formData.acepta_privacidad}
                    onChange={handleChange}
                    className="mt-1 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-slate-300 text-sm">
                    Acepto la <a href="#" className="text-emerald-400 hover:underline">política de privacidad</a>
                  </span>
                </label>

                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="acepta_uso_individual"
                    checked={formData.acepta_uso_individual}
                    onChange={handleChange}
                    className="mt-1 rounded border-slate-600 bg-slate-700 text-emerald-500 focus:ring-emerald-500"
                  />
                  <span className="text-slate-300 text-sm">
                    Entiendo que esta es una cuenta personal e intransferible
                  </span>
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setStep(1)}
                  className="flex-1 bg-slate-700 hover:bg-slate-600 text-white py-3 rounded-xl font-semibold transition-colors"
                >
                  Atrás
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={loading || !formData.acepta_terminos || !formData.acepta_privacidad || !formData.acepta_uso_individual}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-500 disabled:bg-slate-600 disabled:cursor-not-allowed text-white py-3 rounded-xl font-semibold transition-colors"
                >
                  {loading ? 'Registrando...' : 'Crear cuenta'}
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default EunacomLandingPage;
